import { Api } from '@automapa/svelte-automapa-template';
import { DaysWithJobsToProcessApi } from './DaysWithJobsToProcessApi';

/**
 * Klasa zarządzająca cache'owaniem danych o dniach z zadaniami do przetworzenia.
 * Optymalizuje zapytania do API poprzez:
 * - Przechowywanie pobranych danych w cache
 * - Unikanie duplikacji requestów dla tych samych dni
 * - Obsługę równoczesnych requestów (deduplication)
 * - Batching wywołań getDayWithAction (zbieranie przez ~100ms i wykonanie jednego requestu)
 */
export class DaysWithJobsToProcessApp {
    /**
     * Cache przechowujący liczbę zadań dla każdego dnia.
     * Klucz: data w formacie YYYY-MM-DD
     * Wartość: liczba zadań do przetworzenia
     */
    private daysWithJobsToProcess: Map<string, number|(Record<string, number>)> = new Map();

    /**
     * Mapa przechowująca pending promises dla dni, które są aktualnie pobierane.
     * Zapobiega duplikacji requestów dla tego samego dnia.
     */
    private pendingRequests: Map<string, Promise<void>> = new Map();

    /**
     * Kolejka dni oczekujących na przetworzenie w batch.
     * Klucz: dzień (YYYY-MM-DD)
     * Wartość: tablica akcji do wykonania po pobraniu danych
     */
    private batchQueue: Map<string, Array<(jobsQuantity: number|Record<string, number>) => void>> = new Map();

    /**
     * Timer dla mechanizmu batching.
     * Null gdy nie ma zaplanowanego batch requestu.
     */
    private batchTimer: ReturnType<typeof setTimeout> | null = null;

    /**
     * Czas oczekiwania na kolejne wywołania przed wykonaniem batch requestu (ms).
     */
    private batchDelay: number = 100;

    private api: Api;

    private counters: string[] = [];

    constructor(api: Api, counters: string[] = []) {
        this.api = api;
        this.counters = counters;
    }

    /**
     * Pobiera dane dla pojedynczego dnia z API.
     * Używana wewnętrznie przez getDay().
     */
    private async _fetchDay(day: string): Promise<this> {
        const response = await DaysWithJobsToProcessApi.getDaysWithJobsToProcess(this.api, [day], this.counters);
        this.daysWithJobsToProcess.set(day, response[day] ?? this.defaultDayValue());
        return this;
    }

    /**
     * Przetwarza kolejkę batch - pobiera dane dla wszystkich dni w kolejce
     * i wykonuje wszystkie zakolejkowane akcje.
     */
    private async _processBatchQueue(): Promise<void> {
        // Wyczyść timer
        this.batchTimer = null;

        // Pobierz dni z kolejki
        const daysToFetch: string[] = [];
        const actionsMap = new Map<string, Array<(jobsQuantity: number|Record<string, number>) => void>>();

        // Skopiuj kolejkę i wyczyść ją
        for (const [day, actions] of this.batchQueue.entries()) {
            daysToFetch.push(day);
            actionsMap.set(day, actions);
        }
        this.batchQueue.clear();

        if (daysToFetch.length === 0) {
            return;
        }

        // Pobierz dane dla wszystkich dni (wykorzystuje istniejący mechanizm getDays)
        await this.getDays(daysToFetch);

        // Wykonaj wszystkie akcje dla każdego dnia
        for (const [day, actions] of actionsMap.entries()) {
            const jobsQuantity = this.daysWithJobsToProcess.get(day) ?? this.defaultDayValue();
            for (const action of actions) {
                try {
                    action(jobsQuantity);
                } catch (error) {
                    console.error(`Error executing action for day ${day}:`, error);
                }
            }
        }
    }

    /**
     * Pobiera dane dla wielu dni z API w jednym zapytaniu.
     * Używana wewnętrznie przez getDays().
     */
    private async _fetchDays(days: string[]): Promise<this> {
        if (days.length === 0) {
            return this;
        }

        const response = await DaysWithJobsToProcessApi.getDaysWithJobsToProcess(this.api, days, this.counters);

        // Zapisz wszystkie dni z odpowiedzi
        for (const day of days) {
            this.daysWithJobsToProcess.set(day, response[day] ?? this.defaultDayValue());
        }

        return this;
    }

    /**
     * Pobiera dane dla pojedynczego dnia.
     * Jeśli dane są w cache, zwraca je natychmiast.
     * Jeśli request jest już w toku, czeka na jego zakończenie.
     * W przeciwnym razie wysyła nowy request.
     */
    public async getDay(day: string): Promise<this> {
        // Sprawdź czy dane są już w cache (używamy has() zamiast sprawdzania wartości)
        if (this.daysWithJobsToProcess.has(day)) {
            return this;
        }

        // Sprawdź czy request jest już w toku
        const pendingRequest = this.pendingRequests.get(day);
        if (pendingRequest) {
            await pendingRequest;
            return this;
        }

        // Utwórz nowy request i zapisz go jako pending
        const request = this._fetchDay(day)
            .finally(() => {
                // Usuń z pending po zakończeniu (sukces lub błąd)
                this.pendingRequests.delete(day);
            });

        this.pendingRequests.set(day, request.then(() => {}));
        await request;
        return this;
    }

    /**
     * Pobiera dane dla wielu dni.
     * Optymalizuje zapytania poprzez:
     * - Pomijanie dni już obecnych w cache
     * - Czekanie na pending requesty dla dni w trakcie pobierania
     * - Grupowanie pozostałych dni w jedno zapytanie
     */
    public async getDays(days: string[]): Promise<this> {
        if (days.length === 0) {
            return this;
        }

        const missingDays: string[] = [];
        const pendingPromises: Promise<void>[] = [];

        for (const day of days) {
            if (this.daysWithJobsToProcess.has(day)) {
                // Dzień już w cache - pomiń
                continue;
            }

            const pendingRequest = this.pendingRequests.get(day);
            if (pendingRequest) {
                // Request w toku - dodaj do listy oczekujących
                pendingPromises.push(pendingRequest);
            } else {
                // Dzień do pobrania
                missingDays.push(day);
            }
        }

        // Czekaj na wszystkie pending requesty
        if (pendingPromises.length > 0) {
            await Promise.all(pendingPromises);
        }

        // Pobierz brakujące dni w jednym zapytaniu
        if (missingDays.length > 0) {
            const request = this._fetchDays(missingDays)
                .finally(() => {
                    // Usuń wszystkie dni z pending
                    for (const day of missingDays) {
                        this.pendingRequests.delete(day);
                    }
                });

            // Oznacz wszystkie dni jako pending
            const pendingPromise = request.then(() => {});
            for (const day of missingDays) {
                this.pendingRequests.set(day, pendingPromise);
            }

            await request;
        }

        return this;
    }

    /**
     * Pobiera dane dla dnia i wykonuje akcję z liczbą zadań.
     * Użyteczne dla callback-based API.
     *
     * OPTYMALIZACJA: Zamiast natychmiastowego wywołania API, dodaje dzień do kolejki batch.
     * Jeśli w ciągu ~100ms pojawią się kolejne wywołania, zostaną zgrupowane w jeden request.
     *
     * Jeśli dzień jest już w cache, akcja wykonywana jest natychmiast.
     */
    public getDayWithAction(
        day: string,
        action: (jobsQuantity: number|Record<string, number>) => void
    ): this {
        // Jeśli dzień jest już w cache, wykonaj akcję natychmiast
        if (this.daysWithJobsToProcess.has(day)) {
            const jobsQuantity = this.daysWithJobsToProcess.get(day) ?? 0;
            action(jobsQuantity);
            return this;
        }

        // Dodaj akcję do kolejki batch dla tego dnia
        const existingActions = this.batchQueue.get(day);
        if (existingActions) {
            existingActions.push(action);
        } else {
            this.batchQueue.set(day, [action]);
        }

        // Jeśli timer już działa, nie rób nic (czekamy na więcej wywołań)
        // Jeśli timer nie działa, ustaw nowy timer
        if (this.batchTimer === null) {
            this.batchTimer = setTimeout(() => {
                this._processBatchQueue();
            }, this.batchDelay);
        }

        return this;
    }

    /**
     * Zwraca liczbę zadań dla danego dnia z cache.
     * Nie wykonuje zapytania do API.
     * @returns liczba zadań lub undefined jeśli dzień nie jest w cache
     */
    public getCachedDay(day: string): number | Record<string, number> | undefined {
        return this.daysWithJobsToProcess.get(day);
    }

    /**
     * Zwraca wszystkie dane z cache jako obiekt.
     * @returns obiekt z dniami jako kluczami i liczbą zadań jako wartościami
     */
    public getCachedDays(): Record<string, number|Record<string, number>> {
        return Object.fromEntries(this.daysWithJobsToProcess);
    }

    /**
     * Sprawdza czy dzień jest w cache.
     */
    public hasDay(day: string): boolean {
        return this.daysWithJobsToProcess.has(day);
    }

    /**
     * Czyści cache dla konkretnego dnia.
     * Użyteczne gdy wiemy, że dane dla dnia się zmieniły.
     */
    public clearDay(day: string): this {
        this.daysWithJobsToProcess.delete(day);
        return this;
    }

    /**
     * Czyści cache dla wielu dni.
     */
    public clearDays(days: string[]): this {
        for (const day of days) {
            this.daysWithJobsToProcess.delete(day);
        }
        return this;
    }

    /**
     * Czyści cały cache.
     * Użyteczne przy wylogowaniu lub zmianie kontekstu.
     */
    public clearAll(): this {
        this.daysWithJobsToProcess.clear();
        this.pendingRequests.clear();
        this.batchQueue.clear();

        // Anuluj timer batch jeśli istnieje
        if (this.batchTimer !== null) {
            clearTimeout(this.batchTimer);
            this.batchTimer = null;
        }

        return this;
    }

    /**
     * Zwraca liczbę dni w cache.
     */
    public getCacheSize(): number {
        return this.daysWithJobsToProcess.size;
    }

    /**
     * Wymusza ponowne pobranie dnia z API, nawet jeśli jest w cache.
     * Użyteczne gdy wiemy, że dane się zmieniły.
     */
    public async refreshDay(day: string): Promise<this> {
        this.clearDay(day);
        return this.getDay(day);
    }

    /**
     * Wymusza ponowne pobranie wielu dni z API.
     */
    public async refreshDays(days: string[]): Promise<this> {
        this.clearDays(days);
        return this.getDays(days);
    }

    /**
     * Ustawia opóźnienie dla mechanizmu batching.
     * @param delayMs - opóźnienie w milisekundach (domyślnie 100ms)
     */
    public setBatchDelay(delayMs: number): this {
        this.batchDelay = delayMs;
        return this;
    }

    /**
     * Wymusza natychmiastowe przetworzenie kolejki batch.
     * Użyteczne gdy chcemy wymusić wykonanie akcji bez czekania na timer.
     */
    public async flushBatchQueue(): Promise<this> {
        if (this.batchTimer !== null) {
            clearTimeout(this.batchTimer);
            this.batchTimer = null;
        }

        if (this.batchQueue.size > 0) {
            await this._processBatchQueue();
        }

        return this;
    }

    /**
     * Zwraca liczbę dni oczekujących w kolejce batch.
     */
    public getBatchQueueSize(): number {
        return this.batchQueue.size;
    }

    private defaultDayValue(): number|Record<string, number> {
        return this.counters.length ? Object.fromEntries(this.counters.map(key => [key, 0])) : 0;
    }
}
